import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";
import { initGTM } from "./gtm";
import "./styles/globals.css";
import App from "./App.jsx";


const MODE = import.meta.env.MODE;
const APP_ENV = import.meta.env.VITE_APP_ENV ?? "local";
const IS_LOCAL = MODE === "development" || APP_ENV === "local";
const GTM_ID = import.meta.env.VITE_GTM_ID

Sentry.init({
  dsn: "https://194a56fd7876d734938d08eb31ce8e73@o4510066501419008.ingest.us.sentry.io/4510066515705856",
  sendDefaultPii: false,
  environment: APP_ENV,
  enabled: !IS_LOCAL,
});

setOptions({
  key: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
  region: "NZ",
});


const container = document.getElementById("root")
const root = createRoot(container, !IS_LOCAL ? {
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn('Uncaught error', error, errorInfo.componentStack);
  }),
  // Callback called when React catches an error in an ErrorBoundary.
  onCaughtError: Sentry.reactErrorHandler(),
  // Callback called when React automatically recovers from errors.
  onRecoverableError: Sentry.reactErrorHandler(),
} : undefined);

// 👉 Initialize GTM only outside local/dev
if (!IS_LOCAL && GTM_ID) {
  initGTM(GTM_ID);
}


window.addEventListener("error", (event) => {

  const err = event.error instanceof Error
    ? event.error
    : new Error(event.message || "Unhandled error");

  if (event.filename || event.lineno || event.colno) {
    err.fileName = event.filename;
    err.lineNumber = event.lineno;
    err.columnNumber = event.colno;
  }

  Sentry.captureException(err, (scope) => {
    scope.setTag("capture.source", "window.error");
    return scope;
  });
});

// Unhandled promise rejections
window.addEventListener("unhandledrejection", (event) => {
  const reason = event.reason;
  const err = reason instanceof Error
    ? reason
    : new Error(`Unhandled rejection: ${String(reason)}`);

  Sentry.captureException(err, (scope) => {
    scope.setTag("capture.source", "unhandledrejection");
    return scope;
  });
});

(async () => {
  // Load the core/maps libs you need
  await importLibrary("maps");
  await importLibrary("places");


  root.render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
})().catch((err) => {
  console.error("Failed to load Google Maps libraries", err);
  Sentry.captureException(err, (scope) => {
    scope.setTag("capture.source", "maps.importLibrary");
    return scope;
  });
  // Show error message in the DOM
  root.render(<div style={{ padding: 16, color: "red" }}>Maps failed to load.</div>);
});

// --- Register service worker only in production ---
if (import.meta.env.MODE === "production" && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .then((reg) => console.log("✅ Service Worker registered:", reg.scope))
      .catch((err) => console.error("❌ Service Worker registration failed:", err));
  });
}